import type { ReactElement, ReactNode } from 'react';
import SeeMoreDropdown from 'components/technical/SeeMoreDropDown/SeeMoreDropdown';
import CreateUpdatePortfolioDialogItem from './update/CreateUpdatePortfolioDialogItem.tsx';
import { DeletePortfolioMenuItem } from './DeletePortfolioMenuItem';
import { ListItemDecorator, MenuItem } from '@mui/joy';
import { type NavigateFunction, useNavigate } from 'react-router-dom';
import {
  type IConstraintFormulation,
  type ICreateRebalancingRuleInput,
  type IReturnMeasureNameUi,
  type ILeverageType,
  type IPortfolioRebalancingRule,
  type IRebalancedPortfolioMultifactorRuleInput,
  type IRebalancedPortfolioFilter,
  useRecomputePortfolioMutation,
  type IObjectiveType,
  type IGroupConstrainedQuantity,
  type IPrimaryConstrainedQuantity,
  type ISecondaryConstrainedQuantity,
  type IRiskBudgetAllocation,
  type IMultifactor,
} from 'generated/graphql';
import { useFeedback } from 'components/technical/Feedback/UseFeedback';
import type { NotVerifiedAsset } from 'components/market/asset/AssetLabelService';
import type { CreateAccountIdAutocompleteOptionsInputAccount } from 'components/portfolio/account/AccountService';
import type { SubfundAutocompleteOptionInput } from 'components/portfolio/fund/SubFundService';
import { IPortfolioDefinitionType, IPortfolioDefinitionSubType, type IPortfolioDefinition } from 'generated/graphql';
import { Pencil } from 'components/technical/icons';
import isNil from 'lodash/fp/isNil';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PublishIcon from '@mui/icons-material/Publish';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DialogMenuItem } from 'components/technical/DialogDropdown/DialogMenuItem.tsx';
import EditPortfolioTagsDialog from './EditPortfolioTagsDialog.tsx';
import TagIcon from '@mui/icons-material/Tag';

export interface PortfolioDefActionOptimizerRule {
  assetIds: string[];
  filters: IRebalancedPortfolioFilter[];
  optimizationInput: {
    constraintFormulation: IConstraintFormulation;
    returnMeasureName: IReturnMeasureNameUi;
    leverageType: ILeverageType;
    assets: {
      asset: { id: string; symbol: string };
      constraint?: { lowerBound: number; upperBound: number } | null;
    }[];
    groupConstraints: {
      group: { id: string };
      allocationConstraints: {
        constrainedQuantity: IGroupConstrainedQuantity;
        constraint: { lowerBound: number; upperBound: number };
      }[];
    }[];
    objectives: {
      objectiveType: IObjectiveType;
      risk: string;
      benchmark?: { id: string } | null;
    }[];
    portfolioConstraints?: {
      primary?: {
        constrainedQuantity: IPrimaryConstrainedQuantity;
        constraint: { lowerBound: number; upperBound: number };
        riskMetric?: { metricLabel: string; benchmark?: { id: string } | null } | null;
      } | null;
      secondary?: {
        constrainedQuantity: ISecondaryConstrainedQuantity;
        constraint: { lowerBound: number; upperBound: number };
        riskMetric?: { metricLabel: string; benchmark?: { id: string } | null } | null;
      } | null;
    } | null;
    riskBudgetAllocation: IRiskBudgetAllocation;
    multifactor?: IMultifactor | null | undefined;
  };
}

export interface PortfolioDefActionRebalancedDetails {
  transactionTag?: string | null | undefined;
  rebalancingRules: Pick<
    IPortfolioRebalancingRule,
    'id' | 'name' | 'start' | 'end' | 'periodUnit' | 'periodValue' | 'tradingFee'
  >[];
  defaultAssetComposition: {
    asset: {
      id: string;
      symbol: string;
    };
    weight: number;
  }[];
  multifactorRule?: IRebalancedPortfolioMultifactorRuleInput | null | undefined;
  optimizerRule?: PortfolioDefActionOptimizerRule | null | undefined;
}

export type PortfolioDefActionDef = Pick<
  IPortfolioDefinition,
  'createdAt' | 'description' | 'id' | 'name' | 'type' | 'genie' | 'subType' | 'tags'
> & {
  realDefAccounts: { id: string; name: string; venue: { label: string } }[];
  realDefSubFunds: { id: number; dimension: string; name: string }[];
  assets?: NotVerifiedAsset[];
  rebalancedDetails?: PortfolioDefActionRebalancedDetails | null | undefined;
};

interface PortfolioDefinitionActionsProps {
  def: PortfolioDefActionDef;
  accounts: CreateAccountIdAutocompleteOptionsInputAccount[];
  subFunds: SubfundAutocompleteOptionInput[];
  allTags: string[];
}

export interface RebalancedDefaultPortfolioDefinitionActionsState {
  id?: string;
  name: string;
  description: string;
  composition: { assetId: string; symbol: string; weight: number }[];
  rebalancingRules: ICreateRebalancingRuleInput[];
}

export interface RebalancedMultifactorPortfolioDefinitionActionsState {
  id?: string;
  name: string;
  description: string;
  rebalancingRules: ICreateRebalancingRuleInput[];
  multifactor: IRebalancedPortfolioMultifactorRuleInput;
}

export interface RebalancedOptimizerPortfolioDefinitionActionsState {
  id?: string;
  name: string;
  description: string;
  rebalancingRules: ICreateRebalancingRuleInput[];
  optimizerRule: PortfolioDefActionOptimizerRule;
}

const editActionChildren = (
  <>
    <ListItemDecorator>
      <Pencil fontSize="md" />
    </ListItemDecorator>
    Edit
  </>
);

const duplicateActionChildren = (
  <>
    <ListItemDecorator>
      <ContentCopyIcon fontSize="md" />
    </ListItemDecorator>
    Duplicate
  </>
);

const createUpdateAction = ({
  def,
  accounts,
  subFunds,
  navigate,
  duplicate,
}: Omit<PortfolioDefinitionActionsProps, 'allTags'> & {
  navigate: NavigateFunction;
  duplicate: boolean;
}): ReactNode => {
  const rebalancingRules = (def.rebalancedDetails?.rebalancingRules ?? []).map((rule) => {
    const { start, end, id, ...rest } = rule;
    return {
      ...rest,
      startAt: start,
      endAt: end,
    };
  });

  const key = duplicate ? 'duplicate' : 'edit';
  const actionChildren = duplicate ? duplicateActionChildren : editActionChildren;
  const name = duplicate ? `${def.name} (copy)` : def.name;

  if (def.type === IPortfolioDefinitionType.Rebalanced && def.subType === IPortfolioDefinitionSubType.Default) {
    return (
      <MenuItem
        key={key}
        onClick={() => {
          navigate('/app/copilot/portfolio-builder', {
            state: {
              id: duplicate ? undefined : def.id,
              name,
              description: def.description,
              composition: (def.rebalancedDetails?.defaultAssetComposition ?? []).map((asset) => ({
                assetId: asset.asset.id,
                symbol: asset.asset.symbol,
                weight: asset.weight,
              })),
              rebalancingRules,
            } satisfies RebalancedDefaultPortfolioDefinitionActionsState,
          });
        }}
      >
        {actionChildren}
      </MenuItem>
    );
  }

  if (
    def.type === IPortfolioDefinitionType.Rebalanced &&
    def.subType === IPortfolioDefinitionSubType.MultifactorBased
  ) {
    const multifactorRule = def.rebalancedDetails?.multifactorRule;
    if (isNil(multifactorRule)) {
      return null;
    }

    return (
      <MenuItem
        key={key}
        onClick={() => {
          navigate('/app/market/asset-screener', {
            state: {
              id: duplicate ? undefined : def.id,
              name,
              description: def.description,
              multifactor: multifactorRule,
              rebalancingRules,
            } satisfies RebalancedMultifactorPortfolioDefinitionActionsState,
          });
        }}
      >
        {actionChildren}
      </MenuItem>
    );
  }

  if (def.type === IPortfolioDefinitionType.Rebalanced && def.subType === IPortfolioDefinitionSubType.OptimizerBased) {
    const optimizerRule = def.rebalancedDetails?.optimizerRule;
    if (isNil(optimizerRule)) {
      return null;
    }

    const suffix = optimizerRule.assetIds.length > 0 ? 'with-asset-universe' : 'with-asset-screener';

    return (
      <MenuItem
        key={key}
        onClick={() => {
          navigate(`/app/copilot/portfolio-optimizer/optimizations/portfolio-definition-${suffix}`, {
            state: {
              id: duplicate ? undefined : def.id,
              name,
              description: def.description,
              rebalancingRules,
              optimizerRule: optimizerRule,
            } satisfies RebalancedOptimizerPortfolioDefinitionActionsState,
          });
        }}
      >
        {actionChildren}
      </MenuItem>
    );
  }

  if (
    def.type === IPortfolioDefinitionType.Rebalanced &&
    def.subType === IPortfolioDefinitionSubType.ReturnBased &&
    duplicate
  ) {
    return null;
  }

  const { id, ...restWithoutAnId } = def;

  return (
    <CreateUpdatePortfolioDialogItem
      key={key}
      def={{
        id: duplicate ? undefined : def.id,
        ...restWithoutAnId,
        name,
        rebalancedDetails: {
          defaultAssetComposition: (def.rebalancedDetails?.defaultAssetComposition ?? []).map((asset) => ({
            id: asset.asset.id,
            weight: asset.weight,
          })),
          transactionTag: def.rebalancedDetails?.transactionTag ?? '',
          multifactorRule: null,
          optimizerRule: null,
        },
        rebalancingRules,
      }}
      availableAccounts={accounts}
      subFunds={subFunds}
    >
      {actionChildren}
    </CreateUpdatePortfolioDialogItem>
  );
};

const PortfolioDefinitionActions = ({
  def,
  accounts,
  subFunds,
  allTags,
}: PortfolioDefinitionActionsProps): ReactElement => {
  const navigate = useNavigate();
  const [recomputePortfolio] = useRecomputePortfolioMutation();
  const { showGraphqlError, showSuccessMessage } = useFeedback();

  const actions = [
    createUpdateAction({ def, accounts, subFunds, navigate, duplicate: false }),
    createUpdateAction({ def, accounts, subFunds, navigate, duplicate: true }),
  ].filter(Boolean);

  if (
    def.subType === IPortfolioDefinitionSubType.Default ||
    def.subType === IPortfolioDefinitionSubType.MultifactorBased ||
    def.subType === IPortfolioDefinitionSubType.OptimizerBased
  ) {
    actions.push(
      <MenuItem
        key="rebalancing"
        onClick={() => {
          navigate(`/app/copilot/lab/portfolio/${def.id}/rebalancing-rules`);
        }}
      >
        <>
          <ListItemDecorator>
            <ScheduleIcon fontSize="md" />
          </ListItemDecorator>
          Rebalancing rules
        </>
      </MenuItem>
    );
  }
  if (def.subType === IPortfolioDefinitionSubType.PositionBased) {
    actions.push(
      <MenuItem
        key="import-positions"
        onClick={() => {
          navigate(`/app/copilot/lab/portfolio/${def.id}/import-positions`);
        }}
      >
        <>
          <ListItemDecorator>
            <PublishIcon fontSize="md" />
          </ListItemDecorator>
          Import positions
        </>
      </MenuItem>
    );
  }

  if (def.subType === IPortfolioDefinitionSubType.TransactionBased) {
    actions.push(
      <MenuItem
        key="recompute"
        onClick={async () => {
          try {
            await recomputePortfolio({ variables: { id: def.id } });
            showSuccessMessage('Portfolio scheduled for recomputing');
          } catch (e) {
            showGraphqlError(e);
          }
        }}
      >
        <>
          <ListItemDecorator>
            <AutorenewIcon fontSize="md" />
          </ListItemDecorator>
          Recompute
        </>
      </MenuItem>
    );
  }

  actions.push(
    <DialogMenuItem
      key="edit-tags"
      renderDialog={({ onClose }): ReactElement => (
        <EditPortfolioTagsDialog
          onClose={onClose}
          portfolioDefinitionId={def.id}
          currentTags={def.tags}
          allTags={allTags}
        />
      )}
    >
      <ListItemDecorator>
        <TagIcon fontSize="md" />
      </ListItemDecorator>
      Edit tags
    </DialogMenuItem>
  );
  actions.push(<DeletePortfolioMenuItem key="delete" name={def.name} id={def.id} />);

  return <SeeMoreDropdown>{actions}</SeeMoreDropdown>;
};

export default PortfolioDefinitionActions;
